import React, { ChangeEvent, createRef, useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from '../../../hooks'
import { selectPlayerScoreForTrack } from '../../../selectors/match-selectors'
import { PlayerTrackPath, ScoreBonuses, TrackPath } from '../../../types'
import { NumberInputWithLabel } from '../../widgets/TextInputWithLabel'
import { adjustTrackScore } from '../../../actions/updateScore'
import { getScoreRank } from '../../../util/score-utils'
import { MAX_SCORE, Rank, rankEnum } from '../../../constants/constants'
import cx from 'classnames'

type Props = {
  trackPath: TrackPath
  onClose: () => void
}

const scorePartLabels = ['original score', 'scored blue', 'random track', 'hidden lyrics', 'misc']
const PlayStatusDetails = ({ trackPath, onClose }: Props) => {
  const dispatch = useAppDispatch()
  const { compoundMatchSlug, compoundPlayerSlug, trackSlug } = trackPath
  const [matchOwner, matchSlug] = compoundMatchSlug.split('/')
  const [username, player] = compoundPlayerSlug.split('/')
  const playerTrackPath: PlayerTrackPath = {
    matchOwner,
    matchSlug,
    username,
    player,
    trackSlug,
  }
  const { bonuses, topScore, topScoreRank, counters } = useAppSelector(
    selectPlayerScoreForTrack(playerTrackPath)
  )
  const { originalScore, scoredBlue, randomTrack, hiddenLyrics, misc } = bonuses || {
    originalScore: topScore,
  }

  const initialScoreParts = [originalScore, scoredBlue, randomTrack, hiddenLyrics, misc]
  const [dirtyState, setDirtyState] = useState([false, false, false, false, false])
  const [workingScoreParts, setWorkingScoreParts] = useState(initialScoreParts)
  const [workingTotal, setWorkingTotal] = useState(topScore)
  const [workingScoreRank, setWorkingScoreRank] = useState(topScoreRank)

  const scorePartRefs = initialScoreParts.map(() => createRef<HTMLInputElement>())
  const [isDirty, setIsDirty] = useState(false)
  const onSave = () => {
    const bonuses: ScoreBonuses = {
      originalScore: workingScoreParts[0] || topScore,
      scoredBlue: workingScoreParts[1],
      randomTrack: workingScoreParts[2],
      hiddenLyrics: workingScoreParts[3],
      misc: workingScoreParts[4],
    }
    dispatch(adjustTrackScore({ trackPath, totalScore: workingTotal, bonuses }))
    onClose()
  }
  useEffect(() => {
    const anyDirty = dirtyState.findIndex((isDirty) => isDirty) >= 0
    setIsDirty(anyDirty)
    const newTotal = workingScoreParts.reduce((acc: number, value = 0) => acc + value, 0)
    setWorkingTotal(newTotal)
  }, [dirtyState, workingScoreParts])
  const onScorePartChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value, readOnly } = event.target
    if (readOnly) {
      return
    }
    const index = parseInt(id.split('-')[1])
    const intValue = parseInt(value)
    const newValue = isNaN(intValue) || value === '' ? undefined : intValue
    if (index === 0) {
      const newScoreRank = getScoreRank(newValue || 0, MAX_SCORE)
      setWorkingScoreRank(newScoreRank)
    }
    setWorkingScoreParts((orig) => {
      const newValues = [...orig]
      newValues[index] = newValue
      return newValues
    })
    setDirtyState((orig) => {
      const newDirtyState = [...orig]
      newDirtyState[index] = initialScoreParts[index] !== newValue
      return newDirtyState
    })
  }
  const getCounters = () => {
    return (
      <div className="counters">
        {rankEnum.map((rank) => {
          const key = `counter-${rank}`
          const rankCount = counters[rank as Rank]
          return (
            <div key={key}>
              <input className={rank} type="text" tabIndex={-1} readOnly value={rankCount} />
            </div>
          )
        })}
      </div>
    )
  }
  const className = cx('total', workingScoreRank)
  return (
    <div className="playStatusDetails">
      {getCounters()}
      <div className="numbers">
        {workingScoreParts.map((value, index) => (
          <NumberInputWithLabel
            key={`field-${index}`}
            id={`field-${index}`}
            label={scorePartLabels[index]}
            value={value !== undefined ? Math.round(value) : ''}
            onBlur={onScorePartChange}
            onChange={onScorePartChange}
            onRef={scorePartRefs[index]}
          />
        ))}
        <div className={className}>{Math.round(workingTotal)}</div>
      </div>
      <div className="save">
        <button onClick={onSave} disabled={!isDirty}>
          Save
        </button>
        <button onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

export default PlayStatusDetails
